import {
  CurrentPlan,
  DirectDebitForm,
  DirectDebitValidationRequest,
  FormStep,
  OrderForm,
  WorldPayResponse,
} from '@dgx-pr-spa/shared/util';
import { Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import * as fromCompleteOrder from '../store/actions';
import * as fromCompleteOrderQuery from '../store/selectors';
import * as fromCompleteOrderState from '../store/reducers';

@Injectable({
  providedIn: 'root',
})
export class CompleteOrderService {
  constructor(
    public readonly store?: Store<fromCompleteOrderState.CompleteOrderState>
  ) {}

  readonly orderFormState$ = this.store.select(
    fromCompleteOrderQuery.getFormDataState
  );

  readonly formData$ = this.store.select(fromCompleteOrderQuery.getFormData);

  readonly orderFormLoading$ = this.store.select(
    fromCompleteOrderQuery.getFormDataLoading
  );

  readonly orderFormLoaded$ = this.store.select(
    fromCompleteOrderQuery.getFormDataLoaded
  );

  readonly costSummary$ = this.store.select(
    fromCompleteOrderQuery.getCostSummary
  );

  readonly upfrontPaymentArgs$ = this.store.select(
    fromCompleteOrderQuery.getUpfrontPaymentArgs
  );

  readonly upfrontPayment$ = this.store.select(
    fromCompleteOrderQuery.getUpfrontPayment
  );

  readonly directDebitValidationState$ = this.store.select(
    fromCompleteOrderQuery.getDirectDebitValidationState
  );

  readonly directDebitValidationLoading$ = this.store.select(
    fromCompleteOrderQuery.getDirectDebitValidationLoading
  );

  readonly directDebitValidation$ = this.store.select(
    fromCompleteOrderQuery.getDirectDebitValidation
  );

  readonly directDebitValidationError$ = this.store.select(
    fromCompleteOrderQuery.getDirectDebitValidationError
  );

  readonly directDebitAttempts$ = this.store.select(
    fromCompleteOrderQuery.getDirectDebitAttempts
  );

  readonly directDebitPaymentToken$ = this.store.select(
    fromCompleteOrderQuery.getDirectDebitPaymentToken
  );

  readonly directDebitReuse$ = this.store.select(
    fromCompleteOrderQuery.getDirectDebitReuse
  );

  readonly directDebitAutofillAvailable$ = this.store.select(
    fromCompleteOrderQuery.getdirectDebitAutofillAvailable
  );

  readonly directDebitFormState$ = this.store.select(
    fromCompleteOrderQuery.getDirectDebitFormState
  );

  readonly replacementCost$ = this.store.select(
    fromCompleteOrderQuery.replacementCost
  );

  readonly activeFormStep$ = this.store.select(
    fromCompleteOrderQuery.getActiveFormStep
  );

  readonly createOrderState$ = this.store.select(
    fromCompleteOrderQuery.getCreateOrderState
  );

  readonly createOrder$ = this.store.select(
    fromCompleteOrderQuery.getCreateOrder
  );

  readonly createOrderLoading$ = this.store.select(
    fromCompleteOrderQuery.getCreateOrderLoading
  );

  readonly createOrderLoaded$ = this.store.select(
    fromCompleteOrderQuery.getCreateOrderLoaded
  );

  readonly createOrderError$ = this.store.select(
    fromCompleteOrderQuery.getCreateOrderError
  );

  readonly getCreateOrderRequestArgs$ = this.store.select(
    fromCompleteOrderQuery.getCreateOrderRequestArgs
  );
  readonly cardPaymentRedirectState$ = this.store.select(
    fromCompleteOrderQuery.getCardPaymentRedirectState
  );

  readonly cardPaymentRedirectLoaded$ = this.store.select(
    fromCompleteOrderQuery.getCardPaymentRedirectLoaded
  );

  readonly cardPaymentRedirectLoading$ = this.store.select(
    fromCompleteOrderQuery.getCardPaymentRedirectLoading
  );

  readonly cardPaymentRedirectError$ = this.store.select(
    fromCompleteOrderQuery.getCardPaymentRedirectError
  );

  readonly cardPaymentRedirect$ = this.store.select(
    fromCompleteOrderQuery.getCardPaymentRedirect
  );

  readonly worldpayError$ = this.store.select(
    fromCompleteOrderQuery.getWorldPayError
  );

  updateOrderForm(payload?: OrderForm) {
    this.store.dispatch(fromCompleteOrder.updateForm({ payload }));
  }

  resetOrderForm() {
    this.store.dispatch(
      fromCompleteOrder.updateForm({ payload: {} as OrderForm })
    );
  }

  loadDirectDebitValidation(payload: DirectDebitValidationRequest) {
    this.store.dispatch(fromCompleteOrder.addDirectDebitAttempt());
    this.store.dispatch(
      fromCompleteOrder.loadDirectDebitValidation({ payload })
    );
  }

  setDirectDebitValid() {
    this.store.dispatch(
      fromCompleteOrder.loadDirectDebitValidationSuccess({
        payload: { isValid: true },
      })
    );
  }

  loadUpfrontPayment() {
    this.store.dispatch(fromCompleteOrder.loadUpfrontPayment());
  }

  updateDirectDebitConfig(payload: {
    reuse_dd_details: boolean;
    payment_token?: string;
  }) {
    this.store.dispatch(fromCompleteOrder.loadDirectDebitConfig({ payload }));
  }

  resetDirectDebitConfig() {
    this.store.dispatch(fromCompleteOrder.resetDirectDebitConfig());
  }

  isDirectDebitAutofillAvailable(plan = {} as CurrentPlan) {
    const { dd_details } = plan;
    return (
      !!dd_details?.account_number &&
      !!dd_details?.sort_code &&
      !!dd_details?.name &&
      !!dd_details?.payment_token
    );
  }

  resetDirectDebitAttempts() {
    this.store.dispatch(fromCompleteOrder.resetDirectDebitAttempts());
  }

  updateActiveFormStep(activeStep: FormStep) {
    this.store.dispatch(
      fromCompleteOrder.updateActiveFormStep({ payload: activeStep })
    );
  }

  resetDirectDebitValidation() {
    this.store.dispatch(fromCompleteOrder.resetDirectDebitValidation());
  }

  resetDirectDebitForm(form) {
    if (form?.directDebit) {
      this.updateOrderForm({
        delivery: form.delivery,
        directDebit: {} as DirectDebitForm,
        upfrontPayment: null,
      });
    }
  }

  createOrder(showFullLoader = true) {
    this.store.dispatch(
      fromCompleteOrder.loadCreateOrder({
        payload: {
          showFullLoader,
        },
      })
    );
  }

  cacheOrder() {
    this.store.dispatch(fromCompleteOrder.cacheOrder());
  }

  submitOrder() {
    this.store.dispatch(fromCompleteOrder.submitOrder());
  }

  resetCreateOrder() {
    this.store.dispatch(fromCompleteOrder.resetCreateOrder());
    this.store.dispatch(fromCompleteOrder.resetWorldPay());
  }

  loadCardPaymentRedirect(payload) {
    this.store.dispatch(fromCompleteOrder.loadCardPaymentRedirect({ payload }));
  }

  clearPaymentRedirect() {
    this.store.dispatch(fromCompleteOrder.clearPaymentRedirect());
  }

  loadWorldPay() {
    this.store.dispatch(fromCompleteOrder.loadWorldPay());
  }

  loadWorldPaySuccess(payload: WorldPayResponse) {
    this.store.dispatch(fromCompleteOrder.loadWorldPaySuccess({ payload }));
  }

  loadWorldPayFail(payload: WorldPayResponse) {
    this.store.dispatch(fromCompleteOrder.loadWorldPayFail({ payload }));
  }
}
